import React, { useEffect, useMemo, useState } from "react";
import { api, brl } from "@/api";
import { Money, HideMoneyButton } from "@/contexts/HiddenValues";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, CreditCard, Pencil, Plus, UserPlus, Search, Send, Eye, ChevronDown, ChevronUp } from "lucide-react";
import CurrencyInput from "@/components/CurrencyInput";
import Receipt from "@/components/Receipt";
import LeadSelector from "@/components/LeadSelector";
import { useAuth } from "@/AuthContext";

const STATUSES = ["Pendente", "Separando", "Aguardando", "Pago", "Entregue", "Finalizado"];
const PAYMENTS = ["PIX", "Cartão de Crédito", "Cartão de Débito", "Dinheiro", "Rifa", "Outros", "Pendente"];
const STATUSES_NEEDING_PAYMENT = ["Pago", "Entregue", "Finalizado"];
// Pedidos nesses status NUNCA passaram por "Pago" → ao mudar para "Finalizado"
// o usuário precisa escolher a data de contabilização (hoje ou data do pedido).
// Se o pedido já estava em "Pago", pula a escolha (paid_at é a fonte da verdade).
const STATUSES_NEEDING_DATE_CHOICE = ["Pendente", "Separando", "Aguardando"];

export default function Pedidos() {
  const { canSeeFinance } = useAuth();
  const [orders, setOrders] = useState([]);
  const [paymentDialog, setPaymentDialog] = useState(null);
  // Dialog que pergunta em qual data registrar a venda ao finalizar um pedido
  // que ainda não passou por "Pago" (sem paid_at). Default = data de hoje.
  const [finalizeDateDialog, setFinalizeDateDialog] = useState(null);
  const [editing, setEditing] = useState(null); // order being edited
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [store, setStore] = useState({});
  const [customersMap, setCustomersMap] = useState({});
  const [customersList, setCustomersList] = useState([]);
  const [editCustomerSearch, setEditCustomerSearch] = useState("");
  // ── Criação rápida de cliente direto da edição (igual PDV) ───────────
  const blankCustomer = { name: "", phone: "", cpf: "", address: "" };
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState(blankCustomer);
  const [customerBusy, setCustomerBusy] = useState(false);
  const [showReceipt, setShowReceipt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // Quais colunas do kanban estão recolhidas. Persistido em localStorage —
  // o usuário pode esconder colunas que não acompanha (ex: "Cancelado") e
  // a preferência fica salva entre sessões.
  const COLLAPSED_KEY = "gestaoexpert:pedidos:collapsed";
  const [collapsedCols, setCollapsedCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSED_KEY) || "{}"); }
    catch { return {}; }
  });
  const toggleColumn = (status) => {
    setCollapsedCols(prev => {
      const next = { ...prev, [status]: !prev[status] };
      try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const load = async () => {
    const [o, s, c] = await Promise.all([
      api.get("/orders", { params: { history: false } }),
      api.get("/store"),
      api.get("/customers"),
    ]);
    setOrders(o.data);
    setStore(s.data || {});
    const map = {};
    (c.data || []).forEach(cu => { map[cu.id] = cu; });
    setCustomersMap(map);
    setCustomersList(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const shareOnWhatsApp = async (order) => {
    if (!order.payment_token) {
      // Re-fetch para pegar token
      const r = await api.get(`/orders/${order.id}`);
      order = { ...order, payment_token: r.data?.payment_token };
    }
    if (!order.payment_token) { toast.error("Pedido sem link de pagamento. Salve novamente."); return; }
    if (!store.pix_key) { toast.error("Configure sua chave PIX em Dados da Loja → PIX antes de enviar."); return; }

    const origin = window.location.origin;
    const link = `${origin}/pagamento/${order.payment_token}`;
    const msg = `Seu pedido está pronto para pagamento, segue o link:\n\n${link}`;
    const customer = order.customer_id ? customersMap[order.customer_id] : null;
    const phoneDigits = (customer?.phone || "").replace(/\D/g, "");
    // Se tiver telefone do cliente, abre direto conversa; senão, abre WhatsApp para escolher contato
    const waUrl = phoneDigits
      ? `https://wa.me/${phoneDigits.startsWith("55") ? phoneDigits : "55" + phoneDigits}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const handleStatusChange = (order, newStatus) => {
    // Caso 1: pagamento pendente + status "Pago"/"Entregue"/"Finalizado"
    //         → pede forma de pagamento ANTES (continua o fluxo no submit do dialog).
    if (STATUSES_NEEDING_PAYMENT.includes(newStatus) && order.payment_method === "Pendente") {
      setPaymentDialog({ orderId: order.id, status: newStatus, payment: "PIX", order });
      return;
    }
    // Caso 2: pedido em Pendente/Separando/Aguardando indo para Finalizado
    //         → pergunta em qual data contabilizar a venda (hoje vs data do pedido).
    //         Se o pedido já estava em "Pago", pula essa escolha (paid_at já valeu).
    if (newStatus === "Finalizado" && STATUSES_NEEDING_DATE_CHOICE.includes(order.status)) {
      setFinalizeDateDialog({ orderId: order.id, status: newStatus, order });
      return;
    }
    persistUpdate(order.id, { status: newStatus });
  };

  // Confirma a escolha de data ao finalizar pedido sem paid_at.
  const confirmFinalizeDate = async (choice /* "today" | "original" */) => {
    if (!finalizeDateDialog) return;
    const { orderId, status } = finalizeDateDialog;
    await persistUpdate(orderId, { status, finalize_date_choice: choice });
    setFinalizeDateDialog(null);
  };

  const persistUpdate = async (oid, payload) => {
    await api.put(`/orders/${oid}`, payload);
    if (payload.status === "Finalizado") toast.success("Movido para histórico");
    else toast.success("Atualizado");
    load();
  };

  const confirmPayment = async () => {
    const { orderId, status, payment, order } = paymentDialog;
    // Se for finalização vinda de status sem paid_at, encadeia para o dialog de data
    // — primeiro grava o pagamento, depois pergunta a data.
    if (status === "Finalizado" && order && STATUSES_NEEDING_DATE_CHOICE.includes(order.status)) {
      await api.put(`/orders/${orderId}`, { payment_method: payment });
      setPaymentDialog(null);
      setFinalizeDateDialog({ orderId, status, order });
      return;
    }
    await persistUpdate(orderId, { status, payment_method: payment });
    setPaymentDialog(null);
  };

  const remove = async (oid) => {
    if (!window.confirm("Excluir pedido?")) return;
    await api.delete(`/orders/${oid}`); load();
  };

  const openEdit = async (o) => {
    if (products.length === 0) {
      const p = await api.get("/products", { params: { light: true } });
      setProducts(p.data);
    }
    setEditing({
      id: o.id,
      order_number: o.order_number,
      customer_id: o.customer_id || null,
      items: (o.items || []).map(it => ({ ...it })),
      payment_method: o.payment_method || "Pendente",
      discount: Number(o.discount || 0),
      note: o.note || "",
      lead_id: o.lead_id || null,
    });
    setProductSearch("");
    setEditCustomerSearch("");
  };

  // Submit do modal "novo cliente" aberto a partir da edição de pedido.
  // Espelha o fluxo do PDV (Vendas.jsx): cria o cliente via API, adiciona ao
  // cache local e já SELECIONA no pedido em edição.
  const submitNewCustomer = async () => {
    if (!newCustomer.name) return toast.error("Nome obrigatório");
    setCustomerBusy(true);
    try {
      const payload = { ...newCustomer };
      if (!payload.phone) delete payload.phone;
      if (!payload.cpf) delete payload.cpf;
      if (!payload.address) delete payload.address;
      const r = await api.post("/customers", payload);
      setCustomersList(prev => [...prev, r.data]);
      setCustomersMap(prev => ({ ...prev, [r.data.id]: r.data }));
      setEditing(prev => prev ? { ...prev, customer_id: r.data.id } : prev);
      toast.success("Cliente criado");
      setCreatingCustomer(false);
      setNewCustomer(blankCustomer);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar cliente");
    } finally { setCustomerBusy(false); }
  };

  const editTotals = useMemo(() => {
    if (!editing) return { subtotalGross: 0, lineDiscounts: 0, subtotal: 0, total: 0 };
    const subtotalGross = editing.items.reduce((s, x) => s + Number(x.unit_price || 0) * Number(x.quantity || 0), 0);
    const lineDiscounts = editing.items.reduce((s, x) => s + Math.max(0, Number(x.item_discount || 0)), 0);
    const subtotal = subtotalGross - lineDiscounts;
    const total = Math.max(0, subtotal - Number(editing.discount || 0));
    return { subtotalGross, lineDiscounts, subtotal, total };
  }, [editing]);

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products.slice(0, 12);
    const t = productSearch.trim().toLowerCase();
    return products.filter(p => p.name.toLowerCase().includes(t)).slice(0, 12);
  }, [products, productSearch]);

  const editAddProduct = (p, variant = null) => {
    setEditing(prev => {
      const key = `${p.id}-${variant?.name || ""}`;
      const existing = prev.items.find(x => x.product_id === p.id && (x.variant_name || "") === (variant?.name || ""));
      if (existing) {
        return { ...prev, items: prev.items.map(x => x === existing ? { ...x, quantity: x.quantity + 1 } : x) };
      }
      const unit_price = variant ? variant.price : (p.promo_price || p.price);
      const unit_cost = variant ? variant.cost : p.cost;
      return {
        ...prev,
        items: [...prev.items, {
          product_id: p.id, variant_name: variant?.name || null,
          name: p.name + (variant ? ` (${variant.name})` : ""),
          quantity: 1, unit_price, unit_cost,
        }],
      };
    });
  };

  const editUpdQty = (idx, delta) => {
    setEditing(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, Number(it.quantity || 1) + delta) } : it),
    }));
  };
  const editSetQty = (idx, value) => {
    setEditing(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === idx ? { ...it, quantity: Math.max(1, Number(value || 1)) } : it),
    }));
  };
  const editSetUnitPrice = (idx, value) => {
    setEditing(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === idx ? { ...it, unit_price: Number(value || 0) } : it),
    }));
  };
  const editSetUnitCost = (idx, value) => {
    setEditing(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === idx ? { ...it, unit_cost: Math.max(0, Number(value || 0)) } : it),
    }));
  };
  const editSetItemDiscount = (idx, value) => {
    setEditing(prev => ({
      ...prev,
      items: prev.items.map((it, i) => i === idx ? { ...it, item_discount: Math.max(0, Number(value || 0)) } : it),
    }));
  };
  const editRemoveItem = (idx) => {
    setEditing(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (editing.items.length === 0) return toast.error("O pedido precisa ter pelo menos 1 item");
    setEditBusy(true);
    try {
      await api.put(`/orders/${editing.id}`, {
        customer_id: editing.customer_id || null,
        items: editing.items.map(({ name, ...rest }) => ({ ...rest, name })),
        payment_method: editing.payment_method,
        discount: Number(editing.discount || 0),
        note: editing.note,
        lead_id: editing.lead_id || null,
      });
      toast.success("Pedido atualizado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar pedido");
    } finally { setEditBusy(false); }
  };

  const filteredOrders = useMemo(() => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return orders;
    return orders.filter(o => {
      if (String(o.order_number || "").padStart(6, "0").includes(t)) return true;
      if ((o.customer_name || "").toLowerCase().includes(t)) return true;
      if ((o.payment_method || "").toLowerCase().includes(t)) return true;
      if ((o.status || "").toLowerCase().includes(t)) return true;
      if ((o.note || "").toLowerCase().includes(t)) return true;
      const items = o.items || [];
      if (items.some(it =>
        (it.name || "").toLowerCase().includes(t) ||
        (it.variant_name || "").toLowerCase().includes(t)
      )) return true;
      return false;
    });
  }, [orders, searchTerm]);

  const grouped = STATUSES.slice(0, 5).map(s => ({ status: s, items: filteredOrders.filter(o => o.status === s) }));

  return (
    <div className="space-y-8" data-testid="pedidos-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="label-tiny mb-2">Operação</p>
          <h1 className="font-display text-4xl tracking-tight">Pedidos pendentes</h1>
        </div>
        {canSeeFinance && <HideMoneyButton testId="pedidos-hide-money"/>}
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
        <Input
          value={searchTerm}
          onChange={e=>setSearchTerm(e.target.value)}
          placeholder="Buscar por nº, cliente, item, status, pagamento…"
          className="pl-9 h-9 text-sm"
          data-testid="pedidos-search-input"
        />
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
        {grouped.map(col => {
          const isCollapsed = !!collapsedCols[col.status];
          return (
            <div key={col.status} className={`space-y-3 ${isCollapsed ? "xl:col-span-1" : ""}`}>
              <button
                type="button"
                onClick={()=>toggleColumn(col.status)}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-neutral-50 transition"
                data-testid={`column-toggle-${col.status}`}
                title={isCollapsed ? "Expandir coluna" : "Recolher coluna"}
              >
                <span className="flex items-center gap-2">
                  {isCollapsed ? <ChevronDown size={14} className="text-neutral-400"/> : <ChevronUp size={14} className="text-neutral-400"/>}
                  <h3 className="text-sm font-medium">{col.status}</h3>
                </span>
                <span className="text-xs text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded-md min-w-[24px] text-center">{col.items.length}</span>
              </button>
              {!isCollapsed && col.items.map(o => (
              <Card key={o.id} className="p-4 border-[#E5E5E5] shadow-none" data-testid={`order-card-${o.id}`}>
                <div className="text-xs text-neutral-500">#{String(o.order_number).padStart(6,'0')}</div>
                <div className="text-sm font-medium mt-1">{o.customer_name || "Sem cliente"}</div>
                {/* Itens inline (resumo) */}
                <div className="mt-2 space-y-0.5">
                  {(o.items || []).slice(0, 6).map((it, idx) => (
                    <div key={idx} className="text-[11px] text-neutral-600 truncate">
                      • {it.quantity}× {it.name}{it.variant_name ? ` (${it.variant_name})` : ""}
                    </div>
                  ))}
                  {(o.items || []).length > 6 && (
                    <div className="text-[11px] text-neutral-400 italic">+{o.items.length - 6} item(s)…</div>
                  )}
                </div>
                {o.note && (
                  <div className="mt-2 text-[11px] bg-amber-50 border border-amber-100 rounded px-2 py-1 text-amber-800 break-words">
                    <span className="font-semibold">Obs.:</span> {o.note}
                  </div>
                )}
                <div className="font-display text-lg mt-2">{brl(o.total)}</div>
                {canSeeFinance && typeof o.profit === "number" && (
                  <div className="text-[11px] text-green-700 font-medium mt-0.5" data-testid={`order-profit-${o.id}`}>
                    Lucro <Money value={o.profit}/>
                  </div>
                )}
                <div className="text-[10px] text-neutral-400 mt-1">{new Date(o.created_at).toLocaleString('pt-BR')}</div>
                {(o.created_by || o.last_actor) && (
                  <div className="text-[9px] text-neutral-300 italic">
                    {o.created_by && `criado: ${o.created_by}`}
                    {o.created_by && o.last_actor && o.last_actor !== o.created_by && " · "}
                    {o.last_actor && o.last_actor !== o.created_by && `alterado: ${o.last_actor}`}
                  </div>
                )}
                <div className="flex items-center gap-1 mt-2 text-[11px] text-neutral-600">
                  <CreditCard size={11}/> <span>{o.payment_method === "Pendente programado" ? "Crediário" : o.payment_method}</span>
                </div>
                {o.lead_name && (
                  <div className="flex items-center gap-1 mt-1 text-[10px] text-neutral-500" data-testid={`order-lead-${o.id}`}>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300">
                      Lead: {o.lead_name}
                    </span>
                  </div>
                )}
                <Select value={o.status} onValueChange={(v)=>handleStatusChange(o, v)}>
                  <SelectTrigger className="mt-3 h-8 text-xs" data-testid={`order-status-${o.id}`}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-3 gap-1 mt-2">
                  <Button size="sm" variant="outline" onClick={()=>setShowReceipt({ ...o, customer: o.customer_id ? customersMap[o.customer_id] : null })} className="text-xs" data-testid={`receipt-order-${o.id}`}><Eye size={12} className="mr-1"/> Recibo</Button>
                  <Button size="sm" variant="outline" onClick={()=>openEdit(o)} className="text-xs" data-testid={`edit-order-${o.id}`}><Pencil size={12} className="mr-1"/> Editar</Button>
                  <Button size="sm" variant="ghost" onClick={()=>remove(o.id)} className="text-xs text-red-600 hover:text-red-700"><Trash2 size={12} className="mr-1"/> Excluir</Button>
                </div>
                {o.payment_method === "PIX" && !["Pago", "Finalizado", "Cancelada"].includes(o.status) && (
                  <Button
                    size="sm"
                    onClick={()=>shareOnWhatsApp(o)}
                    className="w-full mt-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid={`send-pix-${o.id}`}
                    title={store.pix_key ? "Enviar link de pagamento PIX no WhatsApp" : "Configure sua chave PIX em Dados da Loja"}
                  >
                    <Send size={12} className="mr-1"/> Enviar PIX no WhatsApp
                  </Button>
                )}
              </Card>
            ))}
          </div>
          );
        })}
      </div>

      {/* Payment confirmation dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(o)=>{ if (!o) setPaymentDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Como foi o pagamento?</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500">Selecione a forma de pagamento para registrar no recibo.</p>
          <div>
            <Label>Forma de pagamento</Label>
            <Select value={paymentDialog?.payment || "PIX"} onValueChange={(v)=>setPaymentDialog(p => ({ ...p, payment: v }))}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {PAYMENTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPaymentDialog(null)}>Cancelar</Button>
            <Button onClick={confirmPayment} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="confirm-payment-button">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalize date choice dialog — qual data registrar a venda */}
      <Dialog open={!!finalizeDateDialog} onOpenChange={(o)=>{ if (!o) setFinalizeDateDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="finalize-date-dialog">
          <DialogHeader>
            <DialogTitle>Em qual data registrar esta venda?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500">
            Por padrão a venda é contabilizada no Financeiro e no Histórico pela
            data de hoje (data da finalização). Se este pedido foi pago antes,
            você pode preferir contabilizá-lo na data em que foi gerado.
          </p>
          {finalizeDateDialog?.order?.created_at && (
            <div className="text-xs text-neutral-500 bg-neutral-50 dark:bg-white/5 rounded-md p-3 border border-[#E5E5E5] dark:border-white/10">
              <div>Pedido #{String(finalizeDateDialog.order.order_number || "").padStart(6, "0")}</div>
              <div>Criado em: <strong>{new Date(finalizeDateDialog.order.created_at).toLocaleString('pt-BR')}</strong></div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={()=>confirmFinalizeDate("original")}
              data-testid="finalize-date-original"
            >
              Data do pedido
            </Button>
            <Button
              onClick={()=>confirmFinalizeDate("today")}
              className="bg-[#0A0A0A] hover:bg-black text-white"
              data-testid="finalize-date-today"
            >
              Data de hoje (padrão)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit order dialog */}
      <Dialog open={!!editing} onOpenChange={(o)=>{ if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-order-dialog">
          <DialogHeader>
            <DialogTitle>Editar pedido #{String(editing?.order_number || "").padStart(6,'0')}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {/* Cliente */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Cliente</Label>
                <div className="flex gap-2 mt-2">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
                    <Input
                      value={editCustomerSearch}
                      onChange={e=>setEditCustomerSearch(e.target.value)}
                      placeholder="Buscar cliente por nome ou telefone…"
                      className="pl-9 h-9 text-sm"
                      data-testid="edit-customer-search"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={()=>setCreatingCustomer(true)}
                    title="Cadastrar novo cliente"
                    data-testid="edit-quick-new-customer-button"
                  >
                    <UserPlus size={16}/>
                  </Button>
                </div>
                <Select
                  value={editing.customer_id || "none"}
                  onValueChange={(v)=>setEditing(prev => ({ ...prev, customer_id: v === "none" ? null : v }))}
                >
                  <SelectTrigger className="mt-2" data-testid="edit-customer-select">
                    <SelectValue placeholder="Selecione um cliente"/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem cliente</SelectItem>
                    {(() => {
                      const t = editCustomerSearch.trim().toLowerCase();
                      const filtered = t
                        ? customersList.filter(c =>
                            (c.name || "").toLowerCase().includes(t) ||
                            (c.phone || "").toLowerCase().includes(t) ||
                            (c.cpf || "").toLowerCase().includes(t)
                          )
                        : customersList;
                      const selected = editing.customer_id && !filtered.some(c => c.id === editing.customer_id)
                        ? customersList.find(c => c.id === editing.customer_id)
                        : null;
                      const list = selected ? [selected, ...filtered] : filtered;
                      if (list.length === 0) {
                        return <div className="px-2 py-3 text-xs text-neutral-400 text-center">Nenhum cliente encontrado.</div>;
                      }
                      return list.slice(0, 50).map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}{c.phone ? ` · ${c.phone}` : ""}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>

              {/* Items */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Itens do pedido</Label>
                <div className="border border-[#E5E5E5] rounded-md mt-2 divide-y divide-[#E5E5E5]">
                  {editing.items.length === 0 && <div className="p-4 text-xs text-neutral-400 text-center">Nenhum item — adicione abaixo.</div>}
                  {editing.items.map((it, idx) => {
                    const lineGross = Number(it.unit_price || 0) * Number(it.quantity || 0);
                    const lineDisc = Math.max(0, Number(it.item_discount || 0));
                    const lineTotal = Math.max(0, lineGross - lineDisc);
                    return (
                      <div key={idx} className="p-3" data-testid={`edit-item-${idx}`}>
                        <div className="flex flex-wrap gap-2 items-center">
                          <div className="flex-1 min-w-[160px]">
                            <div className="text-sm font-medium truncate">{it.name}</div>
                            <div className="text-[10px] text-neutral-500">{it.variant_name || "—"}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="icon" variant="outline" className="h-7 w-7" onClick={()=>editUpdQty(idx, -1)}>−</Button>
                            <Input type="number" value={it.quantity} onChange={e=>editSetQty(idx, e.target.value)} className="w-14 h-7 text-center"/>
                            <Button size="icon" variant="outline" className="h-7 w-7" onClick={()=>editUpdQty(idx, 1)}>+</Button>
                          </div>
                          <div className="w-32">
                            <CurrencyInput value={it.unit_price} onChange={(v)=>editSetUnitPrice(idx, v)} data-testid={`edit-item-price-${idx}`}/>
                          </div>
                          <div className="text-sm font-display whitespace-nowrap w-24 text-right">
                            {lineDisc > 0 ? (
                              <>
                                <div className="text-neutral-400 line-through text-[10px] leading-tight">{brl(lineGross)}</div>
                                <div>{brl(lineTotal)}</div>
                              </>
                            ) : brl(lineGross)}
                          </div>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={()=>editRemoveItem(idx)} data-testid={`edit-item-remove-${idx}`}>
                            <Trash2 size={12}/>
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-[#F5F5F5]">
                          {canSeeFinance && (
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 block">Custo desta venda</label>
                              <CurrencyInput value={it.unit_cost || 0} onChange={(v)=>editSetUnitCost(idx, v)} className="h-8 text-sm" data-testid={`edit-item-cost-${idx}`}/>
                            </div>
                          )}
                          <div className={canSeeFinance ? "" : "col-span-2"}>
                            <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 block">Desconto no item (R$)</label>
                            <CurrencyInput value={it.item_discount || 0} onChange={(v)=>editSetItemDiscount(idx, v)} className="h-8 text-sm" data-testid={`edit-item-discount-${idx}`}/>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add product */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Adicionar item</Label>
                <div className="relative mt-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
                  <Input value={productSearch} onChange={e=>setProductSearch(e.target.value)} placeholder="Buscar produto…" className="pl-9" data-testid="edit-product-search"/>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 max-h-[180px] overflow-y-auto">
                  {filteredProducts.map(p => (
                    <button
                      key={p.id}
                      onClick={()=>editAddProduct(p)}
                      className="text-left border border-[#E5E5E5] rounded-md p-2 hover:bg-neutral-50 transition"
                      data-testid={`edit-add-product-${p.id}`}
                    >
                      <div className="text-xs font-medium truncate">{p.name}</div>
                      <div className="text-[10px] text-neutral-500">{brl(p.promo_price || p.price)}</div>
                      {(p.variants || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {p.variants.map(v => (
                            <span key={v.name} role="button" tabIndex={-1} onClick={(e)=>{e.stopPropagation(); editAddProduct(p, v);}} className="text-[9px] px-1.5 py-0.5 bg-neutral-100 rounded hover:bg-neutral-200">{v.name}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                  {filteredProducts.length === 0 && <div className="col-span-3 text-xs text-neutral-400 text-center py-4">Nenhum produto.</div>}
                </div>
              </div>

              {/* Payment & discount */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Forma de pagamento</Label>
                  <Select value={editing.payment_method} onValueChange={(v)=>setEditing({...editing, payment_method: v})}>
                    <SelectTrigger data-testid="edit-payment-method"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {PAYMENTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Desconto geral</Label>
                  <CurrencyInput value={editing.discount} onChange={(v)=>setEditing({...editing, discount: v})} data-testid="edit-discount"/>
                </div>
              </div>

              <div>
                <Label>Observação</Label>
                <Textarea value={editing.note} onChange={e=>setEditing({...editing, note: e.target.value})} rows={2} data-testid="edit-note"/>
              </div>

              <div>
                <Label className="text-xs text-neutral-500">Lead <span className="text-neutral-400 font-normal">(origem — uso interno)</span></Label>
                <LeadSelector value={editing.lead_id} onChange={(v)=>setEditing({...editing, lead_id: v})} compact />
              </div>

              {/* Totals */}
              <div className="border-t border-[#E5E5E5] pt-4 space-y-1">
                {editTotals.lineDiscounts > 0 && (
                  <>
                    <div className="flex justify-between text-xs text-neutral-500"><span>Itens (sem descontos)</span><span>{brl(editTotals.subtotalGross)}</span></div>
                    <div className="flex justify-between text-xs text-red-600"><span>Descontos nos itens</span><span>−{brl(editTotals.lineDiscounts)}</span></div>
                  </>
                )}
                <div className="flex justify-between text-sm"><span className="text-neutral-500">Subtotal</span><span>{brl(editTotals.subtotal)}</span></div>
                {Number(editing.discount || 0) > 0 && (
                  <div className="flex justify-between text-xs text-red-600"><span>Desconto geral</span><span>−{brl(Number(editing.discount || 0))}</span></div>
                )}
                <div className="flex justify-between font-display text-xl pt-1"><span>Total</span><span data-testid="edit-total">{brl(editTotals.total)}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={editBusy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="edit-save-button">
              {editBusy ? "Salvando…" : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showReceipt && <Receipt sale={showReceipt} store={store} wideLayout onClose={()=>setShowReceipt(null)}/>}

      {/* ── Modal de criação rápida de cliente (espelha o PDV) ──────────── */}
      <Dialog open={creatingCustomer} onOpenChange={setCreatingCustomer}>
        <DialogContent className="max-w-md" data-testid="edit-quick-customer-dialog">
          <DialogHeader>
            <DialogTitle>Novo cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input
                value={newCustomer.name}
                onChange={e=>setNewCustomer({...newCustomer, name: e.target.value})}
                data-testid="edit-quick-customer-name"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={newCustomer.phone}
                  onChange={e=>setNewCustomer({...newCustomer, phone: e.target.value})}
                  placeholder="(00) 00000-0000"
                  data-testid="edit-quick-customer-phone"
                />
              </div>
              <div>
                <Label>CPF</Label>
                <Input
                  value={newCustomer.cpf}
                  onChange={e=>setNewCustomer({...newCustomer, cpf: e.target.value})}
                  placeholder="000.000.000-00"
                  data-testid="edit-quick-customer-cpf"
                />
              </div>
            </div>
            <div>
              <Label>Endereço</Label>
              <Input
                value={newCustomer.address}
                onChange={e=>setNewCustomer({...newCustomer, address: e.target.value})}
                data-testid="edit-quick-customer-address"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreatingCustomer(false)}>Cancelar</Button>
            <Button
              onClick={submitNewCustomer}
              disabled={customerBusy}
              className="bg-[#0A0A0A] hover:bg-black text-white"
              data-testid="edit-quick-customer-submit"
            >
              {customerBusy ? "Salvando…" : "Criar cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
